import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import {
  getLiveEffectOverlay,
  LiveBillboardActive,
  LiveEffectEvent,
  LiveEffectOverlay,
  markLiveEffectPlayed,
  pingLiveEffectOverlay,
} from '../../api/payment/live-effect.api';
import { EffectQueue } from './effect-queue';
import { OverlayEffect, OVERLAY_CODES } from './overlay-effects';
import { Billboard } from './billboard';
import { ObsClient } from './obs-client';
import { createObsEffects } from './obs-effects';
import { runWithObsLock, withObsLock } from './obs-lock';
import { canPump, runObsWithTimeout } from './run-with-timeout';
import { retryQuietly } from './retry-quietly';

/** Đọc "#obs=<mật khẩu>&port=4455" (phần sau # không bao giờ gửi lên máy chủ). */
export const parseObsHash = (
  hash: string
): { password: string | null; port: number } => {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const port = Number(params.get('port'));
  return {
    password: params.get('obs'),
    port: Number.isInteger(port) && port > 0 && port < 65536 ? port : 4455,
  };
};

export interface ObsRunner {
  /** hooks.onStart: gọi khi trò thật sự bắt đầu (sau khi giữ được khoá OBS chung), để tính lại hạn chót từ lúc đó. */
  run(
    ev: LiveEffectEvent,
    overlay: LiveEffectOverlay,
    hooks?: { onStart?: () => void }
  ): Promise<'ok' | 'source_missing'>;
  reset(): Promise<void>;
  connected(): boolean;
}

const PING_MS = 30_000;

/** Chờ nối lại SignalR: 2, 4, 8, 16 giây rồi giữ 30 giây, thử mãi không bỏ. */
const signalRBackoffMs = (retryCount: number) =>
  Math.min(30_000, 2000 * 2 ** Math.min(retryCount, 10));

/** Overlay OBS hiệu ứng tương tác: nền trong suốt, chạy lần lượt từng lượt, hiện khung thầu. Không cần đăng nhập (link có ?key=). */
const PageLiveEffectLivestream: React.FC = () => {
  const { contentCreatorId } = useParams();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key') || '';
  const [overlay, setOverlay] = useState<LiveEffectOverlay | null>(null);
  const [current, setCurrent] = useState<LiveEffectEvent | null>(null);
  const [billboard, setBillboard] = useState<LiveBillboardActive | null>(null);
  const queue = useRef(new EffectQueue());
  const overlayRef = useRef<LiveEffectOverlay | null>(null);
  const runningRef = useRef(false);
  const obsRunner = useRef<ObsRunner | null>(null);

  const load = useCallback(async () => {
    if (!contentCreatorId || !key) return;
    try {
      const data = await getLiveEffectOverlay(contentCreatorId, key);
      overlayRef.current = data;
      setOverlay(data);
      setBillboard(data.billboard ?? null);
      data.queued.forEach((ev) => queue.current.push(ev));
    } catch {
      /* khoá sai hoặc mạng lỗi: overlay đứng im, trong suốt */
    }
  }, [contentCreatorId, key]);

  const finish = useCallback(
    (ev: LiveEffectEvent, note?: string) => {
      if (contentCreatorId && ev.triggerId) {
        queue.current.markDone(ev.triggerId);
        const { triggerId } = ev;
        // Mạng chập chờn: gửi lại thêm 2 lần, cách nhau 2 giây.
        retryQuietly(() =>
          markLiveEffectPlayed(contentCreatorId, key, triggerId, note)
        );
      }
      runningRef.current = false;
      setCurrent(null);
    },
    [contentCreatorId, key]
  );

  const pump = useCallback(() => {
    // Chưa tải cấu hình hoặc overlay đang tắt (trang không vẽ gì): giữ lượt trong hàng đợi.
    if (!canPump(runningRef.current, overlayRef.current)) return;
    const ev = queue.current.shift();
    if (!ev) return;
    runningRef.current = true;
    if (OVERLAY_CODES.includes(ev.code ?? '')) {
      setCurrent(ev);
      return;
    }
    const runner = obsRunner.current;
    const cfg = overlayRef.current;
    if (!runner || !cfg || !runner.connected()) {
      finish(ev, 'obs_disconnected');
      return;
    }
    runObsWithTimeout(runner, ev, cfg, (note) => finish(ev, note));
  }, [finish]);

  useEffect(() => {
    load().then(pump);
  }, [load, pump]);

  useEffect(() => {
    const id = window.setInterval(pump, 500);
    return () => window.clearInterval(id);
  }, [pump]);

  useEffect(() => {
    if (!contentCreatorId || !key) return undefined;
    const ping = () =>
      pingLiveEffectOverlay(
        contentCreatorId,
        key,
        !!obsRunner.current?.connected()
      ).catch(() => undefined);
    ping();
    const id = window.setInterval(() => {
      ping();
      load();
    }, PING_MS);
    return () => window.clearInterval(id);
  }, [contentCreatorId, key, load]);

  useEffect(() => {
    if (!contentCreatorId || !key) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(
        `${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${contentCreatorId}`,
        {
          transport: signalR.HttpTransportType.WebSockets,
          skipNegotiation: true,
        }
      )
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) =>
          signalRBackoffMs(ctx.previousRetryCount),
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    connection.on('ReceiveLiveEffect', (ev: LiveEffectEvent) => {
      if (ev.kind === 'play') {
        queue.current.push(ev);
        pump();
      }
      if (ev.kind === 'reset')
        obsRunner.current?.reset().catch(() => undefined);
      if (ev.kind === 'billboard') load();
    });
    connection.onreconnected(() => {
      load().then(pump);
    });
    // Lần nối đầu hỏng hoặc kết nối đóng hẳn: tự thử lại mãi cho tới khi rời trang.
    let stopped = false;
    let attempt = 0;
    let everStarted = false;
    let retryTimer = 0;
    const scheduleStart = () => {
      if (stopped) return;
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(start, signalRBackoffMs(attempt));
      attempt += 1;
    };
    function start() {
      if (stopped) return;
      connection
        .start()
        .then(() => {
          attempt = 0;
          if (everStarted) load().then(pump);
          everStarted = true;
        })
        .catch(scheduleStart);
    }
    connection.onclose(scheduleStart);
    start();
    return () => {
      stopped = true;
      window.clearTimeout(retryTimer);
      connection.stop();
    };
  }, [contentCreatorId, key, load, pump]);

  // Nối OBS khi link có "#obs=…": mật khẩu chỉ đọc từ phần sau #, không gửi đi đâu. Mất kết nối thì thử lại mỗi 15 giây.
  useEffect(() => {
    const { password, port } = parseObsHash(window.location.hash);
    if (!password) return undefined;
    const client = new ObsClient();
    const fx = createObsEffects({
      call: (t, d) => client.call(t, d),
      storage: window.localStorage,
      isConnected: () => client.isConnected,
    });
    let stopped = false;
    let retry = 0;
    const connect = async () => {
      try {
        await client.connect(`ws://127.0.0.1:${port}`, password);
        // Trả hình còn sót từ lần trước xong mới nhận trò mới. Hai nguồn overlay có #obs= trên cùng máy chạy trò mạnh
        // lần lượt qua một khoá chung (bản lưu hình gốc dùng chung).
        await runWithObsLock(() => fx.restoreSnapshot()).catch(() => undefined);
        if (!stopped) obsRunner.current = withObsLock(fx);
      } catch {
        if (!stopped) retry = window.setTimeout(connect, 15_000);
      }
    };
    const watch = window.setInterval(() => {
      if (!client.isConnected && obsRunner.current) {
        obsRunner.current = null;
        if (!stopped) connect();
      }
    }, 5_000);
    connect();
    return () => {
      stopped = true;
      window.clearTimeout(retry);
      window.clearInterval(watch);
      client.close();
      obsRunner.current = null;
    };
  }, []);

  // Overlay bị tắt khi trò nhóm thường đang chạy: trang không vẽ gì, hẹn giờ của trò bị huỷ, nên báo xong luôn để hàng đợi chạy tiếp.
  const enabled = !!overlay?.isEnabled;
  useEffect(() => {
    if (!enabled && current) finish(current);
  }, [enabled, current, finish]);

  if (!overlay?.isEnabled) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'transparent',
        pointerEvents: 'none',
      }}
    >
      {current && (
        <OverlayEffect
          key={current.triggerId ?? ''}
          event={current}
          onDone={() => finish(current)}
          captionRight={
            !!billboard && overlay.billboardPosition.endsWith('-left')
          }
        />
      )}
      {billboard && (
        <Billboard data={billboard} position={overlay.billboardPosition} />
      )}
    </div>
  );
};

export default PageLiveEffectLivestream;
